import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import messages from './messages';
import styles from '../../../pages/orders-batches/orders-batches.module.less';
import { BASE_SCAN_PATH } from '../../../routes/constants';
import Global from '../../../store/global-selectors';
import { initializeScanQueue } from '../../../store/scan-to-print/scan-to-print.actions';

/**
 * Button component for initiating batch order scanning
 * @returns {JSX.Element}
 */
const ScanOrdersButton = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { name: batchName } = useSelector(Global.selectBatchDetailsBatch);
  const isOrdersGridLoading = useSelector(Global.selectOrdersGridIsLoading);
  const totalRecords = useSelector(Global.selectOrdersGridTotalRecords);
  const pageSize = useSelector(Global.selectOrdersGridPageSize);
  const fulfillmentPlanIds = useSelector((state) => {
    const batchDetailsFulfillmentPlanIds = Global.selectOrdersGridOrders(state);
    return batchDetailsFulfillmentPlanIds ?? [];
  });

  const [isDisabled, disabledTooltipMessage] = useMemo(() => {
    if (isOrdersGridLoading || fulfillmentPlanIds.length === 0) {
      return [true, undefined];
    }
    const tooManyOrdersForScanning = totalRecords > pageSize;
    if (tooManyOrdersForScanning) {
      return [
        true,
        // oxlint-disable-next-line react/jsx-key
        <FormattedMessage {...messages.buttonDisabledTooManyOrders} />,
      ];
    }
    return [false, undefined];
  }, [isOrdersGridLoading, fulfillmentPlanIds.length, totalRecords, pageSize]);

  const handleScanOrders = useCallback(() => {
    if (!isDisabled) {
      dispatch(initializeScanQueue(fulfillmentPlanIds, batchName));
      history.push(BASE_SCAN_PATH);
    }
  }, [dispatch, history, fulfillmentPlanIds, batchName, isDisabled]);

  return (
    <Button
      className={styles['action-button']}
      disabled={isDisabled}
      disabledTooltipMessage={disabledTooltipMessage}
      onClick={handleScanOrders}
      theme={THEME_CONSTANTS.tertiary}
    >
      <FormattedMessage {...messages.scanOrdersButton} />
    </Button>
  );
};

export default ScanOrdersButton;
